// Flat lesson attachments live inside practice so existing AI-material isolation is preserved.
export const MEDIA_TYPES = ['image', 'video', 'audio'];
export const MEDIA_ROLES = ['context', 'explanation', 'hint', 'answer'];
export const MEDIA_PLACEMENTS = ['before', 'after'];
export const MAX_LESSON_MEDIA = 60;
export const MAX_MEDIA_BYTES = 160000;
const ID = /^[A-Za-z0-9_-]{1,80}$/;
const fields = new Set(['id', 'type', 'partId', 'activityId', 'questionId', 'role', 'placement', 'order', 'title', 'sourceLabel', 'fallback', 'alt', 'caption', 'imageUrl', 'videoUrl', 'audioItemId', 'poster', 'captions', 'transcript', 'pronunciation']);
const record = v => !!v && typeof v === 'object' && !Array.isArray(v);
const rows = v => Array.isArray(v) ? v : [];
const idOK = v => typeof v === 'string' && ID.test(v);
const limits = { title: 200, sourceLabel: 300, fallback: 1000, alt: 1000, caption: 2000, captions: 12000, transcript: 12000, pronunciation: 4000 };
const youtubeHosts = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com']);
const videoID = /^[A-Za-z0-9_-]{11}$/;

// No data/javascript/blob/protocol-relative sources or expiring signed GCS URLs.
export function isSafeMediaUrl(value) {
    if (typeof value !== 'string' || !value.trim() || value.length > 2048 || /[\u0000-\u001f\u007f\\]/.test(value)) return false;
    const raw = value.trim();
    if (/^\/(?!\/)/.test(raw)) return !raw.includes('..');
    try {
        const url = new URL(raw);
        return url.protocol === 'https:' && !url.username && !url.password && ![...url.searchParams.keys()].some(k => /^(x-goog-|googleaccessid$|signature$)/i.test(k));
    } catch { return false; }
}

// Restrict new lesson attachments to the actual supported provider, independently
// of the permissive legacy YouTube extractor. The iframe receives only this ID.
export function lessonYouTubeId(value) {
    if (typeof value !== 'string' || value.length > 2048) return null;
    const raw = value.trim();
    if (videoID.test(raw)) return raw;
    if (!isSafeMediaUrl(raw) || raw.startsWith('/')) return null;
    try {
        const url = new URL(raw);
        let id;
        if (['youtu.be', 'www.youtu.be'].includes(url.hostname)) {
            if (!/^\/[A-Za-z0-9_-]{11}\/?$/.test(url.pathname)) return null;
            id = url.pathname.split('/')[1];
        } else if (youtubeHosts.has(url.hostname)) {
            if (url.pathname === '/watch') id = url.searchParams.get('v');
            else if (/^\/(embed|shorts)\/[A-Za-z0-9_-]{11}\/?$/.test(url.pathname)) id = url.pathname.split('/')[2];
        }
        return videoID.test(id || '') ? id : null;
    } catch { return null; }
}

export function mediaAssociations(content) {
    const p = record(content?.blocks?.practice) ? content.blocks.practice : {};
    const parts = rows(content?.blocks?.parts).filter(v => record(v) && idOK(v.id));
    const activities = rows(p.activities).filter(v => record(v) && idOK(v.id)).map(v => ({ id: v.id, partId: v.partId, title: v.title || v.id, kind: 'activity' }));
    if (record(p.mission)) {
        for (const step of rows(p.mission.steps)) if (record(step) && idOK(step.id)) activities.push({ id: step.id, partId: p.mission.partId, title: step.title || step.id, kind: 'mission' });
    }
    const questions = rows(p.cards).filter(v => record(v) && idOK(v.id)).map(v => ({ id: v.id, activityId: v.activityId, title: 'Question ' + (v.order || v.id) }));
    for (const activity of activities.filter(v => v.kind === 'mission')) questions.push({ id: activity.id, activityId: activity.id, title: activity.title });
    return { parts, activities, questions };
}

export function mediaErrors(content) {
    const p = content?.blocks?.practice;
    if (!record(p) || p.media === undefined) return [];
    if (p.version !== 1) return ['Lesson media requires practice version 1.'];
    if (!Array.isArray(p.media) || p.media.length > MAX_LESSON_MEDIA) return ['Use a media list with at most ' + MAX_LESSON_MEDIA + ' attachments.'];
    const errors = [], seen = new Set(), { parts, activities, questions } = mediaAssociations(content);
    try { if (new TextEncoder().encode(JSON.stringify(p.media)).length > MAX_MEDIA_BYTES) return ['Keep total lesson media metadata below 160 KB.']; }
    catch { return ['Media requires JSON-safe scalar fields.']; }
    for (const [index, row] of p.media.entries()) {
        const label = 'Media ' + (index + 1) + ': ';
        if (!record(row)) { errors.push(label + 'Use a flat attachment object.'); continue; }
        if (Object.keys(row).some(k => !fields.has(k)) || Object.values(row).some(v => typeof v !== 'string' && !(typeof v === 'number' && Number.isFinite(v)))) errors.push(label + 'Use supported scalar fields only.');
        if (!idOK(row.id) || seen.has(row.id)) errors.push(label + 'Use a unique stable ID.');
        seen.add(row.id);
        if (!MEDIA_TYPES.includes(row.type) || !MEDIA_ROLES.includes(row.role) || !MEDIA_PLACEMENTS.includes(row.placement)) errors.push(label + 'Choose a supported type, role and position.');
        if (!Number.isInteger(row.order) || row.order < 1 || row.order > MAX_LESSON_MEDIA) errors.push(label + 'Use a position between 1 and ' + MAX_LESSON_MEDIA + '.');
        if (!idOK(row.partId) || !parts.some(v => v.id === row.partId)) errors.push(label + 'Choose an existing lesson part.');
        if (row.activityId && (!idOK(row.activityId) || activities.filter(v => v.id === row.activityId && v.partId === row.partId).length !== 1)) errors.push(label + 'The activity must belong to this part.');
        if (row.questionId && (!row.activityId || !idOK(row.questionId) || questions.filter(v => v.id === row.questionId && v.activityId === row.activityId).length !== 1)) errors.push(label + 'The question must belong to this activity.');
        for (const [key, limit] of Object.entries(limits)) if (row[key] !== undefined && (typeof row[key] !== 'string' || row[key].length > limit)) errors.push(label + key + ' must be text of at most ' + limit + ' characters.');
        for (const key of ['imageUrl', 'videoUrl', 'poster']) if (row[key] !== undefined && (typeof row[key] !== 'string' || row[key].length > 2048)) errors.push(label + 'Invalid ' + key + '.');
        const sourceKeys = ['imageUrl', 'videoUrl', 'audioItemId'];
        const expected = { image: 'imageUrl', video: 'videoUrl', audio: 'audioItemId' }[row.type];
        if (sourceKeys.some(k => k !== expected && row[k])) errors.push(label + 'Keep only the source for this media type.');
        if (row.type === 'image' && (!isSafeMediaUrl(row.imageUrl) || typeof row.alt !== 'string' || !row.alt.trim())) errors.push(label + 'An image needs an HTTPS/public local image source and alt text.');
        if (row.type === 'video' && !lessonYouTubeId(row.videoUrl)) errors.push(label + 'Use a supported YouTube watch, short, embed link or video ID.');
        if (row.type === 'audio' && !idOK(row.audioItemId)) errors.push(label + 'Choose an existing audio item ID.');
        if (row.poster && (row.type !== 'video' || !isSafeMediaUrl(row.poster))) errors.push(label + 'A video poster needs an HTTPS/public local image source.');
    }
    return [...new Set(errors)];
}

// Exact target matching prevents part-level media being duplicated at every card.
// Hint/answer sources are filtered BEFORE React creates an image/player/iframe.
export function mediaVisible(content, options = {}) {
    if (!options.partId || mediaErrors(content).length) return [];
    const { partId, activityId = '', questionId = '', placement = 'before', revealed = false, hintShown = false, mode = 'learn' } = options;
    const roles = options.roles || (options.role ? [options.role] : MEDIA_ROLES);
    if (!Array.isArray(roles)) return [];
    const independent = ['independent', 'exam', 'assessment'].includes(mode);
    return rows(content?.blocks?.practice?.media).filter(row =>
        row.partId === partId && (row.activityId || '') === activityId && (row.questionId || '') === questionId &&
        row.placement === placement && roles.includes(row.role) &&
        (row.role !== 'answer' || (!independent && revealed === true)) &&
        (row.role !== 'hint' || (!independent && hintShown === true))
    ).sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}

// Editor mutations retain stable IDs and unrelated rows. In-flight uploads use
// these same pure helpers against the latest value, never an array position.
export function updateMediaRow(value, id, patch) {
    return rows(value).map(row => row.id === id ? { ...row, ...patch } : row);
}
export function moveMediaRow(value, id, direction) {
    const next = rows(value).slice(), index = next.findIndex(row => row.id === id), target = index + direction;
    if (![-1, 1].includes(direction) || index < 0 || target < 0 || target >= next.length) return next;
    [next[index], next[target]] = [next[target], next[index]];
    return next.map((row, i) => ({ ...row, order: i + 1 }));
}

export function existingLessonMedia(content, availableAssets = []) {
    const out = [];
    for (const part of rows(content?.blocks?.parts)) {
        for (const image of rows(part?.images).concat(record(part?.image) ? [part.image] : [])) if (isSafeMediaUrl(image?.imageUrl)) out.push({ type: 'image', imageUrl: image.imageUrl, alt: image.alt || '', caption: image.caption || '', title: image.caption || part.heading || 'Existing image' });
        if (lessonYouTubeId(part?.video)) out.push({ type: 'video', videoUrl: part.video, title: part.heading || 'Existing video' });
    }
    if (isSafeMediaUrl(content?.blocks?.image?.imageUrl)) out.push({ type: 'image', ...content.blocks.image, title: content.blocks.image.caption || 'Lesson image' });
    for (const videoUrl of [content?.blocks?.video, content?.blocks?.introVideo]) if (lessonYouTubeId(videoUrl)) out.push({ type: 'video', videoUrl, title: 'Lesson video' });
    for (const asset of [...rows(content?.blocks?.practice?.media), ...rows(availableAssets)]) {
        if (asset?.type === 'image' && isSafeMediaUrl(asset.imageUrl)) out.push({ type: 'image', imageUrl: asset.imageUrl, alt: asset.alt || '', caption: asset.caption || '', title: asset.title || 'Existing image' });
        if (asset?.type === 'video' && lessonYouTubeId(asset.videoUrl)) out.push({ type: 'video', videoUrl: asset.videoUrl, title: asset.title || 'Existing video' });
    }
    const sourceKey = row => row.type + ':' + (row.type === 'video' ? lessonYouTubeId(row.videoUrl) : row.imageUrl);
    return out.filter((row, i) => out.findIndex(other => sourceKey(other) === sourceKey(row)) === i);
}
